import { Minus, Plus, User } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

/**
 * `showHousehold` is off for the hero's short capture, which asks for contact details only —
 * relationship status and dependents exist to feed the affordability score, and that flow runs no
 * score. Hidden here rather than forked into a second component, so the fields, validation and
 * test ids stay in one place.
 */
const StepPersonalDetails = ({ formData, handleChange, showHousehold = true }) => {
  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="w-14 h-14 bg-[#00D47E]/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <User className="w-7 h-7 text-[#00D47E]" />
        </div>
        <h2 className="font-outfit text-2xl md:text-3xl font-bold text-black mb-2">
          Personal Details
        </h2>
        <p className="text-gray-600">Tell us a bit about yourself</p>
      </div>

      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <Label className="text-gray-700">Full Name</Label>
            <Input
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="John Smith"
              className="mt-2"
              data-testid="full-name-input"
            />
          </div>
          <div className="col-span-2">
            <Label className="text-gray-700">Email</Label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              placeholder="john@example.com"
              className="mt-2"
              data-testid="email-input"
            />
          </div>
          <div className="col-span-2">
            <Label className="text-gray-700">Mobile Number</Label>
            <Input
              type="tel"
              value={formData.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
              placeholder="021 123 4567"
              className="mt-2"
              data-testid="phone-input"
            />
            <p className="text-xs text-gray-500 mt-1.5">
              So a specialist can call you about your options.
            </p>
          </div>
        </div>

        {showHousehold && (
          <>
        <div>
          <Label className="text-gray-700 mb-3 block">Relationship Status</Label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {['single', 'married', 'defacto', 'separated'].map((status) => (
              <button
                key={status}
                onClick={() => handleChange('relationship_status', status)}
                data-testid={`relationship-status-${status}`}
                className={`p-3 rounded-lg border-2 text-sm font-medium capitalize transition-all ${
                  formData.relationship_status === status
                    ? 'border-[#00D47E] bg-[#00D47E]/5 text-[#00D47E]'
                    : 'border-gray-200 hover:border-gray-300 text-gray-600'
                }`}
              >
                {status === 'defacto' ? 'De Facto' : status}
              </button>
            ))}
          </div>
        </div>

        <div>
          <Label className="text-gray-700 mb-2 block">Number of Dependents/Children</Label>
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="icon"
              onClick={() => handleChange('number_of_children', Math.max(0, formData.number_of_children - 1))}
              className="h-10 w-10"
              data-testid="children-decrement-btn"
            >
              <Minus className="w-4 h-4" />
            </Button>
            <span className="text-2xl font-bold w-12 text-center" data-testid="children-count">
              {formData.number_of_children}
            </span>
            <Button
              variant="outline"
              size="icon"
              onClick={() => handleChange('number_of_children', formData.number_of_children + 1)}
              className="h-10 w-10"
              data-testid="children-increment-btn"
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>
        </div>
          </>
        )}
      </div>
    </div>
  );
};

export default StepPersonalDetails;
